.button {
  border: transparent solid 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: var(--transition-button);
  text-transform: var(--text-transform-button);

  --icon-size: 20px;
}

.button:focus-visible {
  outline-offset: var(--spacing-xs);
  outline: var(--content-accent) var(--spacing-xs) solid;
}

.button:disabled:not(.loading) {
  background-color: var(--surface-action-disabled);
  color: var(--content-on-action-disabled);
  border-color: transparent;
  box-shadow: none;
  transform: none;
}

.button:disabled:hover {
  background-color: var(--surface-action-disabled);
  color: var(--content-on-action-disabled);
  border-color: transparent;
  box-shadow: none;
  transform: none;
}

.button:disabled:active {
  transform: none;
}

.ghost {
  background-color: var(--surface-default);
  color: var(--content-on-action-secondary);
}

.primary {
  background-color: var(--surface-action-primary-default);
  color: var(--content-on-action-primary);
  border: var(--border-width-action-primary) solid var(--border-action-primary-default);
  box-shadow: var(--shadow-action-primary-offset-x-default) var(--shadow-action-primary-offset-y-default) 0 0
    var(--shadow-action-primary-default);
}

.primary:visited {
  color: var(--content-on-action-primary);
}

.primary:hover {
  background-color: var(--surface-action-primary-hover);
  color: var(--content-on-action-primary);
  border-color: var(--border-action-primary-hover);
  box-shadow: var(--shadow-action-primary-offset-x-hover) var(--shadow-action-primary-offset-y-hover) 0 0
    var(--shadow-action-primary-hover);
  transform: var(--transform-button-hover);
}

.primary:active {
  background-color: var(--surface-action-primary-active);
  color: var(--content-on-action-primary);
  border-color: var(--border-action-primary-active);
  transform: var(--transform-button-active);
  box-shadow: 0 0 0 0 var(--shadow-action-primary-active);
}

.plusGradient {
  background: linear-gradient(
    295deg,
    var(--brand-plus-gradient-start) 0%,
    var(--brand-plus-gradient-mid) 25%,
    var(--brand-plus-gradient-end) 70%,
    var(--brand-plus-gradient-end) 100%
  );
  color: var(--brand-plus-gradient-content);
  border: none;
  box-shadow: var(--shadow-action-primary-offset-x-default) var(--shadow-action-primary-offset-y-default) 0 0
    var(--shadow-action-primary-default);
}

.plusGradient:visited {
  color: var(--brand-plus-gradient-content);
}

.plusGradient:hover {
  background: linear-gradient(
    295deg,
    var(--brand-plus-gradient-start-hover) 0%,
    var(--brand-plus-gradient-mid-hover) 25%,
    var(--brand-plus-gradient-end-hover) 70%,
    var(--brand-plus-gradient-end-hover) 100%
  );

  /* Plus gradient buttons don't use brand-specific shadow offsets on hover */
  box-shadow: 0 0 0 0 var(--shadow-action-primary-hover);
}

.plusGradient:active {
  background: linear-gradient(
    295deg,
    var(--brand-plus-gradient-start-active) 0%,
    var(--brand-plus-gradient-mid-active) 25%,
    var(--brand-plus-gradient-end-active) 70%,
    var(--brand-plus-gradient-end-active) 100%
  );
  box-shadow: 0 0 0 0 var(--shadow-action-primary-active);
  transform: var(--transform-button-press);
}

.primarySubtle {
  background-color: var(--surface-action-primary-subtle-default);
  color: var(--content-on-action-primary-subtle);
  border: var(--border-width-action-primary-subtle) solid var(--border-action-primary-subtle-default);
  box-shadow: var(--shadow-action-primary-offset-x-default) var(--shadow-action-primary-offset-y-default) 0 0
    var(--shadow-action-primary-subtle-default);
}

.primarySubtle:visited {
  color: var(--content-on-action-primary-subtle);
}

.primarySubtle:hover {
  background-color: var(--surface-action-primary-subtle-hover);
  border-color: var(--border-action-primary-subtle-hover);
  box-shadow: var(--shadow-action-primary-offset-x-hover) var(--shadow-action-primary-offset-y-hover) 0 0
    var(--shadow-action-primary-subtle-hover);
  transform: var(--transform-button-hover);
}

.primarySubtle:active,
.primarySubtle.active:not(.loading, :disabled) {
  background-color: var(--surface-action-primary-subtle-active);
  color: var(--content-on-action-primary-subtle-active);
  border-color: var(--border-action-primary-subtle-active);
  transform: var(--transform-button-active);
  box-shadow: 0 0 0 0 var(--shadow-action-primary-subtle-active);
}

.secondary {
  background-color: var(--surface-action-secondary-default);
  color: var(--content-on-action-secondary);
  border: var(--border-width-action-secondary) solid var(--border-action-secondary-default);
  box-shadow: var(--shadow-action-secondary-offset-x-default) var(--shadow-action-secondary-offset-y-default) 0 0
    var(--shadow-action-secondary-default);
}

.secondary:visited {
  color: var(--content-on-action-secondary);
}

.secondary:hover {
  background-color: var(--surface-action-secondary-hover);
  border-color: var(--border-action-secondary-hover);
  box-shadow: var(--shadow-action-secondary-offset-x-hover) var(--shadow-action-secondary-offset-y-hover) 0 0
    var(--shadow-action-secondary-hover);
  transform: var(--transform-button-hover);
}

.secondary:active,
.secondary.active:not(.loading, :disabled) {
  background-color: var(--surface-action-secondary-active);
  border-color: var(--border-action-secondary-active);
  transform: var(--transform-button-active);
  box-shadow: 0 0 0 0 var(--shadow-action-secondary-active);
}

.secondary:disabled {
  background-color: var(--surface-action-disabled);
  color: var(--content-on-action-disabled);
  border-color: transparent;
  box-shadow: none;
}

.tertiary {
  background-color: transparent;
  color: var(--content-on-action-tertiary);
}

.tertiary:visited {
  color: var(--content-on-action-tertiary);
}

.tertiary:hover {
  color: var(--content-link-hover);
  background-color: var(--surface-action-tertiary-hover);
}

.tertiary:active,
.tertiary.active:not(.loading, :disabled) {
  color: var(--content-link-active);
  background-color: var(--surface-action-tertiary-active);
  transform: var(--transform-button-press);
}

.tertiary:disabled:not(.loading) {
  background-color: transparent;
  color: var(--content-on-action-disabled);
}

.tertiary:disabled:hover {
  background-color: transparent;
  color: var(--content-on-action-disabled);
}

.gray {
  color: var(--content-primary);
  background-color: var(--surface-action-quiet-default);
}

.gray:hover {
  background-color: var(--surface-action-quiet-hover);
}

.gray:active {
  background-color: var(--surface-action-quiet-active);
  transform: var(--transform-button-press);
}

.gray:disabled:hover {
  background-color: var(--surface-action-disabled);
}

.destructive {
  background-color: var(--surface-action-destructive-default);
  color: var(--content-on-action-destructive);
  border: var(--border-width-action-destructive) solid var(--border-action-destructive-default);
  box-shadow: var(--shadow-action-destructive-offset-x-default) var(--shadow-action-destructive-offset-y-default) 0 0
    var(--shadow-action-destructive-default);
}

.destructive:visited {
  color: var(--content-on-action-destructive);
}

.destructive:hover {
  background-color: var(--surface-action-destructive-hover);
  border-color: var(--border-action-destructive-hover);
  box-shadow: var(--shadow-action-destructive-offset-x-hover) var(--shadow-action-destructive-offset-y-hover) 0 0
    var(--shadow-action-destructive-hover);
  transform: var(--transform-button-hover);
}

.destructive:active,
.destructive.active:not(.loading, :disabled) {
  background-color: var(--surface-action-destructive-active);
  border-color: var(--border-action-destructive-active);
  transform: var(--transform-button-active);
  box-shadow: 0 0 0 0 var(--shadow-action-destructive-active);
}

.destructiveTertiary {
  color: var(--content-on-action-destructive-tertiary);
  background-color: transparent;
}

.destructiveTertiary:visited {
  color: var(--content-on-action-destructive-tertiary);
}

.destructiveTertiary:hover {
  background-color: var(--surface-action-destructive-tertiary-hover);
}

.destructiveTertiary:active {
  background-color: var(--surface-action-destructive-tertiary-active);
  transform: var(--transform-button-press);
}

.button.menuItem {
  padding: var(--spacing-md) var(--spacing-md);
  display: flex;
  width: 100%;
  height: unset;
  border-radius: var(--radius-md);
  background: var(--surface-default);
  color: var(--content-primary);
  font: var(--body-md-font);
  letter-spacing: var(--body-md-letter-spacing);
  justify-content: start;
}

.button.menuItem:hover {
  background: var(--surface-action-navigation-hover);
}

.button.destructiveMenuItem {
  padding: var(--spacing-md) var(--spacing-md);
  display: flex;
  width: 100%;
  height: unset;
  border-radius: var(--radius-md);
  background: var(--surface-default);
  color: var(--content-status-error);
  font: var(--body-md-font);
  letter-spacing: var(--body-md-letter-spacing);
  justify-content: start;
}

.button.destructiveMenuItem:active {
  color: var(--content-status-error);
}

.button.destructiveMenuItem:hover {
  background: var(--surface-action-navigation-destructive-hover);
}

.button.destructiveMenuItem:active,
.button.destructiveMenuItem.active:not(.loading, :disabled) {
  color: var(--content-status-error);
}

.button.fit {
  width: auto;
  white-space: nowrap;
}

.button.fill {
  width: 100%;
}

.unstyled {
  all: unset;
  cursor: pointer;
}

/* An `outline`, not a `border`: `.unstyled` is the one variant that never gets `.button`'s
   `border: transparent solid 2px`, and `all: unset` strips any border of its own — so a focus
   border has no reserved space to occupy and grows the control by 2px per side the moment it
   takes focus. `border-radius` stays because `all: unset` zeroes it and the outline follows it;
   it is inert while unfocused, since the resting state has no border or background. */
.unstyled:focus-visible {
  outline: var(--border-width-thick) solid var(--content-accent);
  outline-offset: var(--spacing-2xs);
  border-radius: var(--radius-xs);
}

.xsmall {
  padding: var(--spacing-sm) var(--spacing-md);
  height: var(--size-input-height-xs);
  border-radius: var(--radius-lg);
  font: var(--label-sm-font);

  --icon-size: var(--size-icon-xs);
}

.small {
  padding: var(--spacing-md) var(--spacing-xl);
  height: var(--size-input-height-sm);
  border-radius: var(--radius-xl);
  font: var(--label-md-font);
  letter-spacing: var(--label-md-letter-spacing);

  --icon-size: var(--size-icon-xs);
}

.medium {
  padding: var(--spacing-md) var(--spacing-xl);
  height: var(--size-input-height-md);
  border-radius: var(--radius-2xl);
  font: var(--label-md-font);
  letter-spacing: var(--label-md-letter-spacing);

  --icon-size: var(--size-icon-sm);
}

.button.large {
  padding: var(--spacing-xl) var(--spacing-2xl);
  height: var(--size-input-height-lg);
  border-radius: var(--radius-3xl);
  font: var(--label-lg-font);
  letter-spacing: var(--label-lg-letter-spacing);

  --icon-size: var(--size-icon-md);
}

.loading {
  pointer-events: none;
}

.children {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
}

.loading .children {
  visibility: hidden;
}

.loading .spinner {
  position: absolute;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: no-preference) {
  .loading .spinner {
    animation: spin 0.72s linear infinite;
  }
}

/* Legacy mode: restore master's pill-shaped button border-radius values.
   Buttons used hardcoded pill values on master, but now share --radius-* tokens
   with inputs. Token overrides target the input values (18px), so buttons need
   explicit component-level overrides to restore their original pill shapes.
   :where() on :global zeroes the attribute specificity so the overall specificity
   is just 0-1-0 (one class) — variant rules like .button.menuItem (0-2-0) always win. */
:where(:global([data-dds-tokens="legacy"])) .xsmall {
  border-radius: 16px;
}

:where(:global([data-dds-tokens="legacy"])) .small {
  border-radius: 21px;
}

:where(:global([data-dds-tokens="legacy"])) .medium {
  border-radius: 24px;
}

:where(:global([data-dds-tokens="legacy"])) .button.large {
  border-radius: 30px;
}
